import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Info } from 'lucide-react';
import { INTAKE_CHANNELS, type DuplicateCandidate, type IntakeChannel, type PersonWithTeams } from '@product-portal/shared';
import { Button } from '@/components/ui/button';
import { Field, inputClass, NativeSelect, RequestForm } from '@/features/requests/request-form';
import { usePeople } from '@/hooks/use-product';
import { useProducts, useSameProblem, useSubmitRequest } from '@/hooks/use-requests';
import { shortName } from '@/lib/format';
import { INTAKE_CHANNEL_LABELS } from '@/lib/labels';
import { cn, initials } from '@/lib/utils';

const CHANNELS = INTAKE_CHANNELS.filter((c): c is Exclude<IntakeChannel, 'portal'> => c !== 'portal');

/** « Pour le compte de »: a colleague already known to the portal and attached to a team (ADR-004). */
function RequesterPicker({ value, onChange, error }: { value: PersonWithTeams | null; onChange: (person: PersonWithTeams | null) => void; error?: string }) {
  const [q, setQ] = useState('');
  const people = usePeople(q);
  if (value) {
    return (
      <div className="flex items-center gap-3 rounded-control border border-border bg-surface-subtle px-3.5 py-2.5">
        <span aria-hidden="true" className="flex size-9 items-center justify-center rounded-full bg-avatar text-[13px] font-bold text-avatar-foreground">
          {initials(value.firstName, value.lastName)}
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <strong className="text-sm">
            {value.firstName} {value.lastName}
          </strong>
          <span className="text-xs text-muted-foreground">
            {value.email} · {value.teams.map((t) => t.name).join(', ')}
          </span>
        </span>
        <Button type="button" variant="secondary" size="sm" aria-label="Changer de demandeur" onClick={() => onChange(null)}>
          Changer
        </Button>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <input
        id="demandeur"
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Nom ou email du collaborateur"
        className={inputClass}
        aria-describedby="demandeur-aide"
      />
      <ul className="m-0 flex max-h-56 list-none flex-col overflow-y-auto rounded-control border border-border p-0">
        {(people.data ?? []).map((p) => (
          <li key={p.id} className="border-b border-muted last:border-b-0">
            <button
              type="button"
              onClick={() => onChange(p)}
              className="flex w-full cursor-pointer flex-col items-start gap-0.5 border-0 bg-card px-3.5 py-2 text-left font-[inherit] hover:bg-accent"
            >
              <span className="text-sm font-bold">
                {p.firstName} {p.lastName}
              </span>
              <span className="text-xs text-muted-foreground">
                {p.email} · {p.teams.map((t) => t.name).join(', ')}
              </span>
            </button>
          </li>
        ))}
        {people.data && people.data.length === 0 && <li className="px-3.5 py-2 text-[13px] text-muted-foreground">Aucune personne trouvée.</li>}
      </ul>
      {error && (
        <span role="alert" className="text-xs font-bold text-destructive">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * « Abonner <collaborateur> » (ADR-009): same workflow as screen 02 — the
 * colleague follows the existing request, where the description typed by the
 * PO is added as a public message signed by the PO (arbitrage of 2026-10-19).
 */
function SubscribeColleague({ duplicate, person, join, busy }: { duplicate: DuplicateCandidate; person: PersonWithTeams | null; join: () => void; busy: boolean }) {
  if (!person) return null;
  if (duplicate.isMine) return <span className="text-[13px] font-bold text-foreground-secondary">Demande de {person.firstName}</span>;
  if (duplicate.isSubscribed) return <span className="text-[13px] font-bold text-status-delivered-foreground">{person.firstName} suit cette demande</span>;
  return (
    <Button type="button" variant="outline" size="sm" className="self-start" disabled={busy} onClick={join}>
      Abonner {person.firstName}
    </Button>
  );
}

/**
 * 15 · Saisir pour un collaborateur (ADR-004). The form and its validation are
 * those of screen 02, applied to the COLLEAGUE's teams. The request is in the
 * colleague's name; the PO stays signed in as itself (`created_by`).
 */
export function OnBehalfPage() {
  const navigate = useNavigate();
  const products = useProducts();
  const submit = useSubmitRequest();
  const sameProblem = useSameProblem();
  const [person, setPerson] = useState<PersonWithTeams | null>(null);
  const [channel, setChannel] = useState<Exclude<IntakeChannel, 'portal'>>('phone');
  const [outerErrors, setOuterErrors] = useState<Record<string, string>>({});

  // Products the colleague's teams may submit on (the PO's own access does not count).
  const allowed = useMemo(() => {
    const ids = new Set(person?.teams.flatMap((t) => t.productIds) ?? []);
    return (products.data ?? []).filter((p) => ids.has(p.id));
  }, [products.data, person]);
  const teamsFor = useCallback(
    (productId: string | null) =>
      (person?.teams ?? []).filter((t) => productId === null || t.productIds.includes(productId)).map(({ id, name }) => ({ id, name })),
    [person],
  );
  const extra = useMemo(() => (person ? { requesterId: person.id, intakeChannel: channel } : {}), [person, channel]);

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Link to="/produit/qualification" className="text-[13px] no-underline">
          ← File de qualification
        </Link>
        <h1 className="m-0 text-[26px] font-black tracking-[-0.01em]">Saisir une demande pour un collaborateur</h1>
        <p className="m-0 text-sm text-muted-foreground">
          Pour une demande reçue par téléphone, email, Teams ou en réunion. Elle est créée au nom du collaborateur : il la suit, reçoit les notifications et
          répond aux questions comme s'il l'avait soumise.
        </p>
      </div>

      <RequestForm
        products={allowed}
        teamsFor={teamsFor}
        extra={extra}
        locked={!person}
        impactLabel="Impact pour l'équipe du demandeur"
        descriptionHint="Reprenez les mots du collaborateur. Il verra cette description dans son suivi."
        duplicateIntro="Si l'une correspond, abonnez le collaborateur plutôt que de créer une nouvelle demande."
        duplicateAction={(d, join, busy) => <SubscribeColleague duplicate={d} person={person} join={join} busy={busy} />}
        /* Arbitrage of 2026-10-19: no screen 16 on screen 15, the PO decides from the side panel. */
        promptOnSubmit={false}
        onSameProblem={async (candidate, input, files, place) => {
          await sameProblem.mutateAsync({ candidateId: candidate.id, place, input, files });
          navigate(`/produit/demandes/${candidate.id}`);
        }}
        cancelTo="/produit/qualification"
        onOuterErrors={(issues) => setOuterErrors(Object.fromEntries(issues.map((i) => [String(i.path[0]), i.message])))}
        before={
          <fieldset className="m-0 flex flex-col gap-3.5 rounded-control border border-border bg-surface-subtle p-4">
            <legend className="px-1 text-[13px] font-bold uppercase tracking-[0.08em] text-foreground-secondary">Pour le compte de</legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
              <Field id="demandeur" label="Demandeur" required error={undefined}>
                <RequesterPicker
                  value={person}
                  onChange={(next) => {
                    setPerson(next);
                    setOuterErrors({});
                  }}
                  error={outerErrors.requesterId}
                />
                <span id="demandeur-aide" className="text-xs text-muted-foreground">
                  Recherche parmi les personnes déjà connectées au portail et rattachées à une équipe.
                </span>
              </Field>
              <Field id="canal" label="Reçue par" error={outerErrors.intakeChannel}>
                <NativeSelect id="canal" value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
                  {CHANNELS.map((c) => (
                    <option key={c} value={c}>
                      {INTAKE_CHANNEL_LABELS[c]}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </div>
          </fieldset>
        }
        onSubmit={async (input, files, intent) => {
          const created = await submit.mutateAsync({ input, files });
          navigate(intent === 'qualify' ? `/produit/demandes/${created.id}` : '/produit/qualification');
        }}
        renderActions={(send, submitting) => (
          <>
            <Button type="button" variant="outline" className="min-h-11" disabled={submitting || !person} onClick={() => send('qualify')}>
              Créer et qualifier
            </Button>
            <Button type="button" className="min-h-11" disabled={submitting || !person} onClick={() => send('create')}>
              Créer la demande
            </Button>
          </>
        )}
        aside={
          <section className="flex flex-col gap-3 rounded-card border border-border bg-card px-5 py-4">
            <h2 className="m-0 flex items-center gap-2 text-[15px] font-bold">
              <Info aria-hidden="true" className="size-4" /> Côté collaborateur
            </h2>
            <ul className={cn('m-0 flex flex-col gap-2 pl-5 text-[13px] text-foreground-secondary')}>
              <li>
                La demande apparaît dans « Mes demandes » de {person ? person.firstName : 'la personne'}, avec la mention « Saisie par … ·{' '}
                {INTAKE_CHANNEL_LABELS[channel].toLowerCase()} ».
              </li>
              <li>{person ? shortName(person) : 'Le collaborateur'} reçoit une notification et toutes les notifications suivantes.</li>
              <li>Le délai de qualification de 2 jours ouvrés part de la création.</li>
              <li>La saisie est inscrite dans l'historique de la demande.</li>
            </ul>
            {/* TODO(OPEN-QUESTION notification-opt-out): mock-up 15 lets the PO untick it; nothing documents it.
                The colleague is always notified (arbitrage of 2026-10-19: ticked and disabled). */}
            <label className="flex min-h-8 items-center gap-2.5 text-[13px]">
              <input type="checkbox" checked disabled readOnly className="m-0" /> Prévenir le collaborateur par email
            </label>
          </section>
        }
      />
    </>
  );
}
